/**
 * The trial runner: one state machine for both presentations.
 *
 * Time comes in from outside (`now`), so tests can drive it deterministically
 * and the page can drive it from `requestAnimationFrame`. The runner never
 * touches the DOM and never asks a presentation anything: presentations are
 * listeners. That is what makes criterion 15 provable — swap the listener,
 * and every foreperiod, window and score stays the same.
 *
 * What it records per trial is the whole point of the stage:
 *   intendedOnset       when the foreperiod said the stimulus should appear
 *   frameAt             the animation-frame time at which the runner asked for it
 *   observedOnset       the first time the host could observe the change committed
 *                       (the next frame after the draw) — an UPPER BOUND on when
 *                       JavaScript can know; NOT physical display onset, which only
 *                       an external reference can see (docs/beacon-measurement.md)
 *   response.eventTs    the input event's own timestamp
 *   response.receivedAt when the handler ran; the gap is event staleness
 *   frame stalls        gaps in the frame clock during the foreperiod
 *   visibility / focus  per trial
 */

import { PROTOCOL, type PresentationVersion, type Protocol } from './protocol'
import { seeded, uniformInt } from './random'

export type Outcome = 'response' | 'lapse' | 'false_start' | 'timeout'

export interface TrialRecord {
  index: number
  foreperiodMs: number
  armedAt: number
  intendedOnset: number
  frameAt: number | null
  observedOnset: number | null
  /** observedOnset − intendedOnset, when both exist. */
  schedulingLagMs: number | null
  frameStallMs: number
  response: { eventTs: number; receivedAt: number; staleMs: number } | null
  rtMs: number | null
  outcome: Outcome | null
  visible: boolean
  focused: boolean
  /** A trial whose onset cannot be trusted (stall, missing observation, stale event). */
  onsetUncertain: boolean
}

export type AttemptState = 'idle' | 'armed' | 'lit' | 'between' | 'ended' | 'interrupted'
export type InterruptReason = 'hidden' | 'blur' | 'stale_event' | 'stopped'

export interface Summary {
  protocolVersion: string
  presentationVersion: string
  mode: 'practice' | 'measurement'
  trials: number
  responses: number
  lapses: number
  falseStarts: number
  timeouts: number
  validTrials: number
  meanRtMs: number | null
  medianRtMs: number | null
  sdRtMs: number | null
  /** Mean of 1/RT in 1/s — the PVT's preferred speed measure. */
  meanSpeed: number | null
  medianSchedulingLagMs: number | null
  p95SchedulingLagMs: number | null
  trialsWithStall: number
  staleEvents: number
  /** Enough valid trials and not interrupted. */
  valid: boolean
  invalidReason: string | null
}

export interface RunnerEvents {
  armed: (trial: TrialRecord) => void
  lit: (trial: TrialRecord) => void
  trialEnd: (trial: TrialRecord) => void
  ended: (summary: Summary) => void
  interrupted: (reason: InterruptReason, trials: TrialRecord[]) => void
  tick: (elapsedMs: number) => void
}

export interface RunnerOptions {
  seed: number
  mode: 'practice' | 'measurement'
  presentation: PresentationVersion
  protocol?: Protocol
  /** Environment probes, injected so tests can lie about them. */
  isVisible?: () => boolean
  isFocused?: () => boolean
}

export class TrialRunner {
  readonly protocol: Protocol
  readonly presentation: PresentationVersion
  readonly mode: 'practice' | 'measurement'
  readonly seed: number
  private readonly rand: () => number
  private readonly isVisible: () => boolean
  private readonly isFocused: () => boolean
  private readonly handlers: { [K in keyof RunnerEvents]: Set<RunnerEvents[K]> } = {
    armed: new Set(),
    lit: new Set(),
    trialEnd: new Set(),
    ended: new Set(),
    interrupted: new Set(),
    tick: new Set(),
  }
  state: AttemptState = 'idle'
  readonly trials: TrialRecord[] = []
  private startedAt: number | null = null
  private current: TrialRecord | null = null
  private lastFrameAt: number | null = null
  private awaitingObservation = false

  constructor(opts: RunnerOptions) {
    this.protocol = opts.protocol ?? PROTOCOL
    this.presentation = opts.presentation
    this.mode = opts.mode
    this.seed = opts.seed
    this.rand = seeded(opts.seed)
    this.isVisible = opts.isVisible ?? (() => true)
    this.isFocused = opts.isFocused ?? (() => true)
  }

  on<K extends keyof RunnerEvents>(event: K, handler: RunnerEvents[K]): () => void {
    this.handlers[event].add(handler)
    return () => {
      this.handlers[event].delete(handler)
    }
  }

  private emit<K extends keyof RunnerEvents>(event: K, ...args: Parameters<RunnerEvents[K]>): void {
    for (const h of [...this.handlers[event]]) (h as (...a: Parameters<RunnerEvents[K]>) => void)(...args)
  }

  get durationMs(): number {
    return this.mode === 'practice' ? this.protocol.practiceDurationMs : this.protocol.durationMs
  }

  elapsed(now: number): number {
    return this.startedAt === null ? 0 : now - this.startedAt
  }

  start(now: number): void {
    if (this.state !== 'idle') return
    this.startedAt = now
    this.lastFrameAt = now
    this.arm(now)
  }

  private arm(now: number): void {
    const foreperiodMs = uniformInt(this.rand, this.protocol.foreperiodMs.min, this.protocol.foreperiodMs.max)
    const trial: TrialRecord = {
      index: this.trials.length,
      foreperiodMs,
      armedAt: now,
      intendedOnset: now + foreperiodMs,
      frameAt: null,
      observedOnset: null,
      schedulingLagMs: null,
      frameStallMs: 0,
      response: null,
      rtMs: null,
      outcome: null,
      visible: this.isVisible(),
      focused: this.isFocused(),
      onsetUncertain: false,
    }
    this.current = trial
    this.trials.push(trial)
    this.state = 'armed'
    this.awaitingObservation = false
    this.emit('armed', trial)
  }

  /**
   * One animation frame. The host calls this with the frame's timestamp, and
   * after drawing a lit stimulus calls `observed()` from the NEXT frame.
   */
  frame(now: number): void {
    if (this.state === 'idle' || this.state === 'ended' || this.state === 'interrupted') return
    if (!this.isVisible()) return this.interrupt('hidden')
    if (!this.isFocused()) return this.interrupt('blur')
    this.emit('tick', this.elapsed(now))

    const t = this.current
    if (this.lastFrameAt !== null && t && this.state === 'armed') {
      const gap = now - this.lastFrameAt
      if (gap > this.protocol.frameStallMs) t.frameStallMs = Math.max(t.frameStallMs, gap)
    }
    this.lastFrameAt = now

    if (this.state === 'armed' && t && now >= t.intendedOnset) {
      t.frameAt = now
      if (t.frameStallMs > this.protocol.frameStallMs) t.onsetUncertain = true
      this.state = 'lit'
      this.awaitingObservation = true
      this.emit('lit', t)
      return
    }
    if (this.state === 'lit' && t && t.frameAt !== null && now - t.frameAt >= this.protocol.timeoutMs) {
      this.finishTrial(t, 'timeout', null, now)
      return
    }
    if (this.state === 'between' && t === null) {
      this.next(now)
    }
  }

  /** The first frame after the lit draw was committed: the host's best observation of onset. */
  observed(now: number): void {
    const t = this.current
    if (!this.awaitingObservation || !t || t.frameAt === null) return
    this.awaitingObservation = false
    t.observedOnset = now
    t.schedulingLagMs = now - t.intendedOnset
    // A response that already arrived measured against an onset we had not yet
    // observed: keep the record honest rather than re-time it.
    if (t.response && t.rtMs !== null && t.outcome === 'response') t.onsetUncertain = true
  }

  /**
   * A response. `eventTs` is the input event's own `timeStamp`; `receivedAt`
   * is the clock when the handler ran. Both on the same monotonic base.
   */
  respond(eventTs: number, receivedAt: number): void {
    if (this.state === 'idle' || this.state === 'ended' || this.state === 'interrupted') return
    const staleMs = receivedAt - eventTs
    const t = this.current
    if (staleMs > this.protocol.staleEventMs) {
      if (t) t.response = { eventTs, receivedAt, staleMs }
      return this.interrupt('stale_event')
    }
    if (this.state === 'armed' && t) {
      t.response = { eventTs, receivedAt, staleMs }
      this.finishTrial(t, 'false_start', null, receivedAt)
      return
    }
    if (this.state === 'lit' && t) {
      const onset = t.observedOnset ?? t.frameAt ?? t.intendedOnset
      if (t.observedOnset === null) t.onsetUncertain = true
      const rt = eventTs - onset
      t.response = { eventTs, receivedAt, staleMs }
      if (rt < this.protocol.falseStartMaxMs) return this.finishTrial(t, 'false_start', rt, receivedAt)
      this.finishTrial(t, rt >= this.protocol.lapseMs ? 'lapse' : 'response', rt, receivedAt)
    }
  }

  private finishTrial(t: TrialRecord, outcome: Outcome, rt: number | null, now: number): void {
    t.outcome = outcome
    t.rtMs = rt
    this.current = null
    this.awaitingObservation = false
    this.emit('trialEnd', t)
    if (this.elapsed(now) >= this.durationMs) {
      this.state = 'ended'
      this.emit('ended', this.summary())
    } else {
      this.state = 'between'
    }
  }

  private next(now: number): void {
    this.arm(now)
  }

  /** The person stops, or the page loses the conditions the protocol needs. Never resumed. */
  interrupt(reason: InterruptReason): void {
    if (this.state === 'ended' || this.state === 'interrupted') return
    this.state = 'interrupted'
    this.current = null
    this.emit('interrupted', reason, this.trials)
  }

  summary(): Summary {
    const done = this.trials.filter((t) => t.outcome !== null)
    const rts = done.filter((t) => (t.outcome === 'response' || t.outcome === 'lapse') && t.rtMs !== null && !t.onsetUncertain).map((t) => t.rtMs!)
    const lags = done.filter((t) => t.schedulingLagMs !== null).map((t) => t.schedulingLagMs!)
    const validTrials = done.filter((t) => !t.onsetUncertain && (t.outcome === 'response' || t.outcome === 'lapse' || t.outcome === 'false_start')).length
    const interrupted = this.state === 'interrupted'
    const enough = validTrials >= this.protocol.minValidTrials
    return {
      protocolVersion: this.protocol.version,
      presentationVersion: this.presentation.version,
      mode: this.mode,
      trials: done.length,
      responses: done.filter((t) => t.outcome === 'response').length,
      lapses: done.filter((t) => t.outcome === 'lapse' || t.outcome === 'timeout').length,
      falseStarts: done.filter((t) => t.outcome === 'false_start').length,
      timeouts: done.filter((t) => t.outcome === 'timeout').length,
      validTrials,
      meanRtMs: mean(rts),
      medianRtMs: quantile(rts, 0.5),
      sdRtMs: sd(rts),
      meanSpeed: rts.length ? mean(rts.map((r) => 1000 / r)) : null,
      medianSchedulingLagMs: quantile(lags, 0.5),
      p95SchedulingLagMs: quantile(lags, 0.95),
      trialsWithStall: done.filter((t) => t.frameStallMs > this.protocol.frameStallMs).length,
      staleEvents: this.trials.filter((t) => t.response !== null && t.response.staleMs > this.protocol.staleEventMs).length,
      valid: !interrupted && enough && this.mode === 'measurement',
      invalidReason: interrupted
        ? 'interrupted'
        : this.mode === 'practice'
          ? 'practice never enters a history'
          : enough
            ? null
            : `only ${validTrials} valid trials; ${this.protocol.minValidTrials} needed`,
    }
  }
}

function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null
}
function sd(xs: number[]): number | null {
  if (xs.length < 2) return null
  const m = mean(xs)!
  return Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / (xs.length - 1))
}
export function quantile(xs: number[], q: number): number | null {
  if (!xs.length) return null
  const s = [...xs].sort((a, b) => a - b)
  const pos = (s.length - 1) * q
  const lo = Math.floor(pos)
  const hi = Math.ceil(pos)
  return s[lo]! + (s[hi]! - s[lo]!) * (pos - lo)
}
